import { Drawer, Tabs } from 'antd';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useStores } from '@/hooks/use-stores';
import CloseIcon from '@/comp/common/icons/CloseIcon';
import MinimizeIcon from '@/comp/common/icons/MinimizeIcon';
import EPIconButton from '@/components/EPButton/EPIconButton';
import { useResponsive } from '@/hooks/useResponsive';
import { useDeviceDrawerActions } from '@/hooks/useDeviceDrawerActions';

import { Container } from './DetailsDrawer.styles';
import { Entity } from '../NodeWidget.types';
import ActivityIndication from './ActivityIndication/ActivityIndication';
import ControllableIndication from './ControllableIndication/ControllableIndication';
import { DrawerContentContainer } from './DetailsDrawer.styles';
import {
  getTabItems,
  getAssetNameDetails,
  shouldShowFooter,
  shouldShowMinimizeButton,
  getConnectivityIcon,
} from './DetailsDrawer.utils';
import PackageCareIndication from './PackageCareIndication/PackageCareIndication';
import PoweredOnIndication from './PoweredOnIndication/PoweredOnIndication';
import Footer from './Footer/Footer';
import { getDeviceIconAndLabel } from '@/comp/StationControlCenter/UniversalDeviceWidget/UniversalDeviceWidget.utils';

interface DetailsDrawerProps {
  isDetailsModalOpen: boolean;
  entity: Entity;
  fromSCC?: boolean;
}

const DetailsDrawer = observer(({ isDetailsModalOpen, entity, fromSCC }: DetailsDrawerProps) => {
  const { mapBuilderStore, stationStore, pidEditStore } = useStores();
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { closeDeviceDrawer } = useDeviceDrawerActions();
  const { deviceType } = entity;
  const isFooterVisible = shouldShowFooter(entity, fromSCC);
  const isMinimizeButtonVisible = shouldShowMinimizeButton(isMobile, fromSCC, pidEditStore.isInEditMode);

  const currentAsset = stationStore.assetsDevices?.find(
    device => device.deviceId === mapBuilderStore.selectedDevice?.deviceId,
  );
  const connectivityIcon = getConnectivityIcon(deviceType, entity, currentAsset, t);
  const { icon: DeviceIcon, label: DeviceLabel } = getDeviceIconAndLabel(deviceType);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation();

  return (
    <Drawer open={isDetailsModalOpen} onClose={closeDeviceDrawer} width={664} closeIcon={false} destroyOnClose>
      <DrawerContentContainer $inMaintenance={entity.inMaintenance} onMouseDown={onMouseDown} $isMobile={isMobile}>
        {entity.inMaintenance && <div className="maintenance-banner">{t('Maintenance')}</div>}

        <Container $isMobile={isMobile} $fromSCC={fromSCC}>
          <div className="drawer-details-header">
            <PackageCareIndication entity={entity} />
            <div className="drawer-details-header-buttons">
              {isMinimizeButtonVisible && (
                <EPIconButton icon={<MinimizeIcon />} onClick={mapBuilderStore.minimizeModal} />
              )}
              <EPIconButton icon={<CloseIcon />} onClick={closeDeviceDrawer} />
            </div>
          </div>
          <div className="first-row">
            <ControllableIndication entity={entity} />
            <PoweredOnIndication entity={entity} />
          </div>
          <div className="second-row">
            <div className="left">
              <div className="device-icon-and-type">
                <div className="device-icon">{DeviceIcon}</div>
                <div className="device-type">{DeviceLabel}</div>
              </div>
              <div className="device-info">
                <div className="device-name">{entity.name ?? null}</div>
                <div className="device-model">{getAssetNameDetails(entity)}</div>
              </div>
            </div>
            <div className="right">
              <div className="connectivity-icon-wrapper">{connectivityIcon}</div>
              <div className="activity-icon-wrapper">
                <ActivityIndication entity={entity} />
              </div>
            </div>
          </div>
          <Tabs
            items={getTabItems(entity, t, pidEditStore.isInEditMode)}
            defaultActiveKey={mapBuilderStore.activeTab}
          />
        </Container>

        {isFooterVisible && (
          <Footer
            inMaintenance={entity?.inMaintenance}
            poweredOn={entity?.poweredOn}
            deviceId={entity?.deviceId}
            deviceType={deviceType}
          />
        )}
      </DrawerContentContainer>
    </Drawer>
  );
});

export default DetailsDrawer;
